import { useCallback, useEffect, useMemo, useState } from "react";
import { Contact, Loader2, Search } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PROFILE_UPDATED_EVENT } from "@/components/admin/SidebarProfileFooter";

const BASE = process.env.REACT_APP_BACKEND_URL;
const ALL = "all";

const assetUrl = (u) => (!u ? "" : /^https?:\/\//i.test(u) ? u : `${BASE}${u}`);
const initials = (name) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
const roleLabel = (m) => (m.positions.length ? m.positions.join(", ") : m.role.replace(/_/g, " "));
const AUTHED_PHOTO = "/api/team-wall/photo/";

// Driver photos stored inline are behind login, so fetch them with the admin token.
const usePhotoSrc = (url) => {
  const authed = !!url && url.startsWith(AUTHED_PHOTO);
  const [blobSrc, setBlobSrc] = useState("");
  useEffect(() => {
    if (!authed) return undefined;
    let objectUrl = "";
    adminApi.get(url.slice(4), { responseType: "blob" })
      .then(({ data }) => { objectUrl = URL.createObjectURL(data); setBlobSrc(objectUrl); })
      .catch(() => setBlobSrc(""));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url, authed]);
  return authed ? blobSrc : assetUrl(url);
};

const openProfile = (m, meId) => {
  window.dispatchEvent(new CustomEvent("rtg:open-dossier", { detail: { driverId: m.id, self: m.id === meId } }));
};

const MemberCard = ({ m, meId, index }) => {
  const src = usePhotoSrc(m.photo_url);
  return (
  <button
    type="button"
    onClick={() => openProfile(m, meId)}
    data-testid={`team-wall-card-${m.id}`}
    style={{ animationDelay: `${Math.min(index, 24) * 25}ms` }}
    className="group text-left rounded-xl overflow-hidden bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-white/10 hover:border-amber-400/70 hover:-translate-y-0.5 hover:shadow-lg transition-[transform,border-color,box-shadow] duration-200 animate-in fade-in slide-in-from-bottom-1"
  >
    <div className="aspect-square bg-zinc-100 dark:bg-zinc-800 grid place-items-center overflow-hidden">
      {src
        ? <img src={src} alt={m.name} loading="lazy" className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-300"/>
        : <span className="text-3xl font-black text-amber-600 dark:text-amber-300">{initials(m.name)}</span>}
    </div>
    <div className="p-3 space-y-0.5 min-w-0">
      <div className="text-sm font-bold text-zinc-900 dark:text-white truncate" data-testid={`team-wall-name-${m.id}`}>{m.name}</div>
      <div className="text-[11px] font-mono text-zinc-500 dark:text-white/50 truncate" data-testid={`team-wall-emp-${m.id}`}>Emp ID: {m.employee_id ? `#${m.employee_id}` : "—"}</div>
      <div className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 truncate capitalize" title={roleLabel(m)} data-testid={`team-wall-role-${m.id}`}>{roleLabel(m)}</div>
    </div>
  </button>
  );
};

const WallSection = ({ title, members, meId, testId }) => (members.length ? (
  <section className="space-y-3" data-testid={testId}>
    <h2 className="text-[11px] uppercase tracking-[0.2em] font-black text-zinc-500 dark:text-white/50">{title} · {members.length}</h2>
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 2xl:grid-cols-6 gap-4">
      {members.map((m, i) => <MemberCard key={m.id} m={m} meId={meId} index={i}/>)}
    </div>
  </section>
) : null);

export const TeamPhotoWall = () => {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [role, setRole] = useState(ALL);

  const load = useCallback(async () => {
    try { const { data: d } = await adminApi.get("/admin/team-wall"); setData(d); } catch { setData({ members: [], error: true }); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener(PROFILE_UPDATED_EVENT, load);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, load);
  }, [load]);

  const members = useMemo(() => data?.members || [], [data]);
  const roles = useMemo(() => [...new Set(members.flatMap((m) => m.positions))].sort(), [members]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return members.filter((m) => (role === ALL || m.positions.includes(role))
      && (!q || m.name.toLowerCase().includes(q) || m.employee_id.toLowerCase().includes(q)));
  }, [members, query, role]);

  return (
    <div className="space-y-6" data-testid="team-photo-wall">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-zinc-900 dark:text-white flex items-center gap-2" data-testid="team-wall-title">
            <Contact className="h-7 w-7 text-amber-500"/>Team Photo Wall
          </h1>
          <p className="text-sm text-zinc-500 mt-1" data-testid="team-wall-count">{data ? `${shown.length} of ${members.length} people` : "Loading the team…"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name or Emp ID" className="pl-9" data-testid="team-wall-search"/>
          </div>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-full sm:w-56" data-testid="team-wall-role-filter"><SelectValue placeholder="All roles"/></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL} data-testid="team-wall-role-all">All roles</SelectItem>
              {roles.map((r) => <SelectItem key={r} value={r} data-testid={`team-wall-role-opt-${r.replace(/\W+/g, "-").toLowerCase()}`}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {!data ? (
        <div className="py-16 grid place-items-center text-zinc-400"><Loader2 className="h-6 w-6 animate-spin"/></div>
      ) : shown.length === 0 ? (
        <div className="py-16 text-center text-sm text-zinc-500" data-testid="team-wall-empty">{data.error ? "Couldn't load the team. Try again shortly." : "No one matches that search."}</div>
      ) : (
        <>
          <WallSection title="Office Staff" members={shown.filter((m) => m.office)} meId={data.me_id} testId="team-wall-office"/>
          <WallSection title="Drivers" members={shown.filter((m) => !m.office)} meId={data.me_id} testId="team-wall-drivers"/>
        </>
      )}
    </div>
  );
};
